/**
 * FRONT-510: Finder-grade selection model for the Memory entity list.
 *
 * Pure, framework-free helpers so the multi-select semantics (toggle,
 * Shift-range, anchor tracking) can be unit-tested without a DOM. The list
 * component holds a `SelectionState` in React state and feeds clicks through
 * `reduceSelection`.
 */

export interface SelectionState {
  /** Ids the user has selected. */
  selected: Set<string>;
  /** The last single-clicked row — the pivot for a subsequent Shift-range. */
  anchor: string | null;
}

export interface SelectionClick {
  id: string;
  /** Shift held → extend a contiguous range from the anchor to this row. */
  shift: boolean;
}

export function emptySelection(): SelectionState {
  return { selected: new Set(), anchor: null };
}

/**
 * The inclusive contiguous slice of `orderedIds` between the anchor and the
 * target, in either direction. Falls back to just the target if either id is
 * no longer in the list (e.g. the filter changed under the user).
 */
export function computeRange(
  orderedIds: string[],
  anchorId: string,
  targetId: string,
): string[] {
  const a = orderedIds.indexOf(anchorId);
  const b = orderedIds.indexOf(targetId);
  if (a === -1 || b === -1) return [targetId];
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return orderedIds.slice(lo, hi + 1);
}

/**
 * Apply a selection click. Shift-click (with a live anchor) unions the range
 * from the anchor to the target onto the current selection — Finder extends,
 * it never replaces. A plain checkbox click or ⌘/Ctrl-click toggles just that
 * row and moves the anchor to it.
 */
export function reduceSelection(
  state: SelectionState,
  click: SelectionClick,
  orderedIds: string[],
): SelectionState {
  const { id, shift } = click;

  if (shift && state.anchor) {
    const range = computeRange(orderedIds, state.anchor, id);
    const selected = new Set(state.selected);
    for (const r of range) selected.add(r);
    return { selected, anchor: state.anchor };
  }

  const selected = new Set(state.selected);
  if (selected.has(id)) selected.delete(id);
  else selected.add(id);
  return { selected, anchor: id };
}

/** Copy for the bulk-delete confirm dialog. Pure so the wording — pinned
 * warning, singular/plural noun — can be unit-tested without a dialog. */
export interface DeleteConfirmCopy {
  title: string;
  description: string;
}

/**
 * Build the confirm-dialog copy for deleting `ids`. Surfaces how many of the
 * targeted entities are pinned (so the user isn't surprised) and pluralizes
 * the noun. Behaviour mirrors the inline wording it replaced (FRONT-515).
 */
export function buildDeleteConfirm(
  ids: string[],
  entities: ReadonlyArray<{ id: string; pinned?: boolean }>,
): DeleteConfirmCopy {
  const idSet = new Set(ids);
  const pinnedCount = entities.filter((e) => idSet.has(e.id) && e.pinned).length;
  const noun = ids.length === 1 ? 'entity' : 'entities';
  return {
    title: `Delete ${ids.length} ${noun}?`,
    description:
      (pinnedCount > 0
        ? `${pinnedCount} of these ${pinnedCount === 1 ? 'is' : 'are'} pinned. `
        : '') + 'This cannot be undone — their relationships are removed too.',
  };
}

/**
 * Select every id in the current (filtered/searched) view, or clear the
 * selection if all of them are already selected — the ⌘A / header-checkbox
 * toggle.
 */
export function toggleSelectAll(
  state: SelectionState,
  orderedIds: string[],
): SelectionState {
  const allSelected =
    orderedIds.length > 0 && orderedIds.every((id) => state.selected.has(id));
  if (allSelected) return emptySelection();
  return { selected: new Set(orderedIds), anchor: state.anchor };
}

/**
 * FRONT-796: the signed-in user's own person entry (id === their Zitadel user
 * id) can never be moved to the recycle bin, so it is dropped from any
 * bulk-delete request before it is sent. Unknown user id → nothing dropped.
 */
export function isOwnPersonEntry(entityId: string, selfId: string | undefined): boolean {
  return !!selfId && entityId === selfId;
}

export function withoutOwnPersonEntry(ids: string[], selfId: string | undefined): string[] {
  return ids.filter((id) => !isOwnPersonEntry(id, selfId));
}
